/**
 * `/database/objects`, `/database/objects/new`, `/database/objects/$kind` —
 * RO-3-11 (#44304), part 3 of 4.
 *
 * Port of `excellent/app/database/objects/page.tsx` (67 LOC over `_index-client.tsx`,
 * 194), `.../objects/new/page.tsx` (27 over `_new-client.tsx`, 433 + `actions.ts`, 61)
 * and `.../objects/[kind]/page.tsx` (67 over `_detail-client.tsx`, 224).
 *
 * ── This is where this item paid a debt it was assigned ──
 *
 * `docs/rollover/inventory/shared-render.md` §5 records exactly one thing as owed
 * by RO-3-11: an `objects.list` Operation returning the object-kind inventory.
 * RO-3-00 had already ported `_index-client.tsx` into
 * `client/shared/database/objects-index-client.tsx` (five callers across four
 * sections), but it could only take `rows` as a PROP, because the Next page had
 * assembled them from `@excellent/core/server` inline and no endpoint existed.
 *
 * `objectTypes.list` is that Operation. `<ObjectsIndex>` below is its first
 * self-populating caller, and the three `*ScopeActions` beside it in
 * `client/shared/database/` — waiting on the same rows for RO-3-04 / RO-3-06 /
 * RO-3-07 — are unblocked by it too.
 *
 * The index component itself is IMPORTED from `client/shared/`, not re-ported.
 * That is the contract `client/shared/index.ts` publishes ("if a component you
 * need is exported below, it is ported … a second copy in your route file is the
 * exact failure this item exists to prevent"), and honouring it is what keeps five
 * callers on one implementation.
 *
 * ── The detail page's `notFound()` ──
 *
 * `objectTypes.detail` 404s on an unknown kind, which is the same refusal the
 * server page made with `notFound()` — the check stays server-side, where the
 * registry lives. The SPA renders it as an inline empty state for the reason
 * `database-editor.tsx` gives at more length: there is no framework 404 boundary
 * here, and inventing a shell-level one is not a page port's call.
 */

import { createRoute } from "@tanstack/react-router";
import { DodPredicatesTable, EmptyState, PageHeader } from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { objectTypes } from "@excellent/ui/api/contracts/database/object-types";
import { rootRoute } from "./__root";
import { ObjectsIndexClient } from "../shared";
import { ObjectTypeDetailClient } from "./database/object-type-detail-client";
import { NewObjectTypeWizard } from "./database/new-object-type-wizard";

// ── /database/objects — the whole schema as one inventory ────────────────────
//
// "The first screen that makes 'Excellent is a data-design tool' visibly true"
// (Data Design PRD §3.4) — every entity kind, built-in AND data-defined, in one
// read-only list. `ObjectsIndexClient` owns the search box + DataTable and routes
// only data-defined kinds to their detail page; the ORDER (user's nouns first) is
// contractual and comes from the endpoint.

function ObjectsIndex() {
  const { data, error, isLoading } = useApi(objectTypes.list, {});

  // The loading / error line renders HERE rather than being pushed into
  // `ObjectsIndexClient` as a new prop. Adding one would mean editing an RO-3-00
  // file that four other section items also consume, to solve a problem only this
  // caller has — and rendering `rows={[]}` while the read is in flight would flash
  // that component's "no object kinds" EmptyState, which is a claim, not a wait.
  if (error) {
    return (
      <div className="flex flex-col gap-4 px-6 py-8">
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      </div>
    );
  }
  if ((isLoading && !data) || !data) {
    return (
      <div className="flex flex-col gap-4 px-6 py-8">
        <p className="type-body text-muted-foreground">Loading…</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      <ObjectsIndexClient rows={data.rows} />
      <div className="px-6 pb-8">
        <DodPredicatesTable />
      </div>
    </div>
  );
}

export const databaseObjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/objects",
  component: ObjectsIndex,
});

// ── /database/objects/new — the creation wizard ──────────────────────────────

function NewObjectType() {
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        <PageHeader
          title="New object type"
          description="Design a new kind of thing to track — no migration required. It becomes a first-class object with its own page, notes, and tags."
        />
        <NewObjectTypeWizard />
      </div>
    </div>
  );
}

export const databaseObjectsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/objects/new",
  component: NewObjectType,
});

// ── /database/objects/$kind — one kind's definition, fields and records ──────

function ObjectTypeDetail() {
  const { kind } = databaseObjectTypeRoute.useParams();
  const { data, error, isLoading } = useApi(objectTypes.detail, { query: { kind } });

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        {isLoading && !data ? <p className="type-body text-muted-foreground">Loading…</p> : null}
        {error ? (
          <EmptyState
            title={`No object type “${kind}”`}
            description={error.message ?? "It may have been removed. Pick one from the Objects list."}
          />
        ) : null}
        {data ? (
          <ObjectTypeDetailClient type={data.type} fields={data.fields} records={data.records} />
        ) : null}
      </div>
    </div>
  );
}

export const databaseObjectTypeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/objects/$kind",
  component: ObjectTypeDetail,
});
